/**
 * Orella Health — Standardized Date Formatting Utilities
 * v166.7.0 — All display times pinned to America/Los_Angeles (PST/PDT)
 * Standard format: YYYY-MM-DD HH:MM (24h) for data, human-readable for display
 * 
 * Usage:
 *   import { formatDate, formatDateTime, formatAppointmentTime, parseDate, ORELLA_TZ } from '../utils/dateUtils'
 */

import { appLocale } from './appLanguage.js'

/** Universal Orella timezone — all user-facing times display in LA time */
export const ORELLA_TZ = 'America/Los_Angeles'

/**
 * PROD-1368: THE wall-time helper. An appointment's `date` (Postgres `date`, 'YYYY-MM-DD') and
 * `time` (Postgres `time without time zone`, 'HH:MM[:SS]'; a typed request may also say '1:00 PM')
 * are the practice's WALL CLOCK in ORELLA_TZ. The API reads them the same way (orella-api
 * `_appt_local_start`: `datetime(y, m, d, hh, mm, tzinfo=ZoneInfo(ORELLA_LOCAL_TZ))`). This turns
 * that pair into the true instant, whatever zone the device is in, so a check-in window or a visit
 * end compared against Date.now() opens and closes at the same moment for every patient.
 * `new Date(y, mo - 1, d, h, m)` reads the same numbers in the DEVICE zone and is wrong everywhere
 * outside Pacific time.
 * DST rule (the API's: Python zoneinfo fold=0, which is also Temporal's 'compatible'):
 *   - a time that occurs twice (2026-11-01 01:30 at fall-back) is the FIRST occurrence (PDT, -07:00);
 *   - a time that does not exist (2026-03-08 02:30 at spring-forward) moves forward by the gap
 *     (read with the offset before the switch, so it is 03:30 PDT).
 * @param {string} dateStr 'YYYY-MM-DD' (anything after the 10th character is ignored)
 * @param {string} timeStr 'HH:MM', 'HH:MM:SS' or 'h:MM AM|PM'
 * @param {string} [zone] defaults to ORELLA_TZ
 * @returns {Date|null} the instant, or null for a missing or impossible date or time
 */
const WALL_FMT = {}
const zoneOffsetMs = (ms, zone) => {
  const f = WALL_FMT[zone] || (WALL_FMT[zone] = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }))
  const p = {}
  for (const { type, value } of f.formatToParts(new Date(ms))) p[type] = value
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second) - Math.floor(ms / 1000) * 1000
}
export const wallTimeToInstant = (dateStr, timeStr, zone = ORELLA_TZ) => {
  const dm = String(dateStr || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
  const ts = String(timeStr || '').trim()
  const tm = ts.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/) || ts.match(/^(\d{1,2}):(\d{2})()\s*([AaPp])\.?[Mm]\.?$/)
  if (!dm || !tm) return null
  let h = +tm[1]
  const mi = +tm[2]
  if (tm[4]) {
    if (h < 1 || h > 12) return null
    h = (h % 12) + (/[Pp]/.test(tm[4]) ? 12 : 0)
  }
  if (h > 23 || mi > 59 || +(tm[3] || 0) > 59) return null
  const [y, mo, d] = [+dm[1], +dm[2], +dm[3]]
  const wall = Date.UTC(y, mo - 1, d, h, mi)
  const check = new Date(wall)
  if (isNaN(wall) || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) return null
  try {
    const before = zoneOffsetMs(wall - 12 * 3600000, zone)
    const after = zoneOffsetMs(wall + 12 * 3600000, zone)
    const fits = (off) => zoneOffsetMs(wall - off, zone) === off
    const candidates = [before, after].filter(fits).map((off) => wall - off)
    // Two fits: the time occurs twice, take the earlier instant. None: it falls in the gap, read it
    // with the offset in force before the switch (moves it forward by the gap).
    return new Date(candidates.length ? Math.min(...candidates) : wall - before)
  } catch {
    return null
  }
}

/**
 * Parse a date string safely, handling timezone issues.
 * Ensures dates like "2024-01-15" don't shift due to UTC interpretation.
 * @param {string|Date} dateString - Date in various formats
 * @returns {Date|null} Parsed Date object or null if invalid
 */
export const parseDate = (dateString) => {
  if (!dateString) return null

  try {
    if (dateString instanceof Date) {
      return isNaN(dateString.getTime()) ? null : dateString
    }

    const str = String(dateString).trim()

    // Handle YYYY-MM-DD (with optional T time) — most common from API
    const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (isoMatch) {
      const [, year, month, day] = isoMatch
      // If there's a time component, parse the full ISO string
      if (str.includes('T')) {
        const date = new Date(str)
        return isNaN(date.getTime()) ? null : date
      }
      // Date-only: create at noon local time to avoid timezone edge cases
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0)
      return isNaN(date.getTime()) ? null : date
    }

    // ★ v242.149.0 — EDI (X12 271) dates arrive as CCYYMMDD with no separators.
    //   An 8-digit run is unambiguous, and the payer discovery card was printing
    //   it raw: "DOB on file 19840211". Parsed at local noon like every other
    //   date-only value, so it cannot slide a day west of UTC.
    const ediMatch = str.match(/^(\d{4})(\d{2})(\d{2})$/)
    if (ediMatch) {
      const [, y, m, d] = ediMatch
      const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d), 12, 0, 0)
      return isNaN(date.getTime()) ? null : date
    }

    // Handle MM/DD/YYYY format
    const usMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (usMatch) {
      const [, month, day, year] = usMatch
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0)
      return isNaN(date.getTime()) ? null : date
    }

    // Fallback to native parsing
    const date = new Date(str)
    return isNaN(date.getTime()) ? null : date
  } catch {
    return null
  }
}

/**
 * PROD-1294: ONE rule for every display helper. A calendar date (date-only, CCYYMMDD, MM/DD/YYYY, or a
 * wall-clock timestamp with no offset) names a day in the visit's own zone: it is held at UTC and
 * formatted with timeZone 'UTC', so no device zone can slide it. An instant (Z or an offset, 2-digit
 * offsets like "+00" / "-07" included) is formatted in the visit zone (ORELLA_TZ by default).
 * @returns {{date: Date, timeZone: string}|null}
 */
const CALENDAR_RE = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ](\d{2})(?::(\d{2}))?(?::(\d{2}))?(?:\.\d+)?$)/
const resolveDisplay = (dateString, zone = ORELLA_TZ) => {
  if (!dateString) return null
  if (typeof dateString === 'string') {
    const str = dateString.trim()
    let y, mo, d, h = 12, mi = 0, sec = 0
    let m = str.match(CALENDAR_RE)
    if (m) {
      ;[, y, mo, d] = m
      if (m[4] !== undefined) { h = +m[4]; mi = +(m[5] || 0); sec = +(m[6] || 0) }
    } else if ((m = str.match(/^(\d{4})(\d{2})(\d{2})$/))) {
      ;[, y, mo, d] = m
    } else if ((m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/))) {
      ;[, mo, d, y] = m
    }
    if (y !== undefined) {
      const date = new Date(Date.UTC(+y, +mo - 1, +d, h, mi, sec))
      // PROD-1330: Date.UTC rolls 2026-02-31 over to Mar 3 and 2026-13-45 into 2027. A calendar day
      // that does not exist is invalid, never a different day.
      if (isNaN(date.getTime()) || date.getUTCMonth() !== +mo - 1 || date.getUTCDate() !== +d || h > 23 || mi > 59 || sec > 59) return null
      return { date, timeZone: 'UTC' }
    }
    // Instant with a space separator and/or a 2-digit offset ("2026-10-20 03:00:00+00"): normalise for Date.
    const inst = str.match(/^(\d{4}-\d{2}-\d{2})[T ](.*?)(Z|[+-]\d{2}(?::?\d{2})?)$/)
    if (inst) {
      const off = /^[+-]\d{2}$/.test(inst[3]) ? inst[3] + ':00' : inst[3]
      const date = new Date(`${inst[1]}T${inst[2]}${off}`)
      return isNaN(date.getTime()) ? null : { date, timeZone: zone }
    }
  }
  const date = parseDate(dateString)
  return date ? { date, timeZone: zone } : null
}

/**
 * PROD-1330: THE calendar-day helper. Every screen that prints a date-only value (visit date, slot
 * date, check-in date) goes through this, never through `new Date('YYYY-MM-DD')` plus a zone. A
 * date-only value is held at UTC noon and formatted with timeZone 'UTC', so 2026-11-03 prints Nov 3
 * for every viewer. An instant (a timestamp WITH Z or an offset) is formatted in ORELLA_TZ. A
 * timestamp WITHOUT an offset ('2026-03-08T00:00:00') is a wall-clock time, not an instant: like a
 * date-only value it is held at UTC and formatted with timeZone 'UTC' (the PROD-1294 rule), so it
 * prints its own calendar day. A Date object is an instant (ORELLA_TZ). Missing or malformed -> ''.
 * @param {string|Date} value
 * @param {Object} options - Intl.DateTimeFormat options
 * @param {string} [locale] - defaults to the app language
 * @returns {string}
 */
export const formatCalendarDay = (value, options = { month: 'short', day: 'numeric', year: 'numeric' }, locale) => {
  const r = resolveDisplay(value)
  if (!r) return ''
  try {
    return r.date.toLocaleDateString(locale || appLocale(), { ...options, timeZone: r.timeZone })
  } catch {
    return ''
  }
}

/**
 * PROD-1330: the label of a month grid ("November 2026"). `monthIndex` is 0-based, like
 * Date#getMonth. The month is named through formatCalendarDay on its first day, never through
 * `new Date(year, month, 1)` formatted in a zone (local midnight on the 1st is still the previous
 * month in Los Angeles for every viewer east of it). Out of range -> ''.
 */
export const formatCalendarMonth = (year, monthIndex, options = { month: 'long', year: 'numeric' }, locale) => {
  const y = Number(year)
  const mi = Number(monthIndex)
  if (!Number.isInteger(y) || !Number.isInteger(mi) || y < 1000 || y > 9999 || mi < 0 || mi > 11) return ''
  return formatCalendarDay(`${y}-${String(mi + 1).padStart(2, '0')}-01`, options, locale)
}

/** PROD-1330: the month group a date-only slot belongs to ("December 2026"). Malformed -> ''. */
export const calendarMonthKey = (dateStr, locale = 'en-US') =>
  formatCalendarDay(dateStr, { month: 'long', year: 'numeric' }, locale)

/**
 * PROD-1330: "Nov 3 – Dec 1, 2026" for the first and last date-only slot of a window. The year is
 * the last day's own year. Either day malformed -> ''.
 */
export const calendarRangeLabel = (firstDateStr, lastDateStr, locale = 'en-US') => {
  // PROD-1369: the last day carries its own year through Intl, so the word order follows the locale
  // (en 'Dec 1, 2026', es '1 dic 2026'); the en result is the same text as before.
  const first = formatCalendarDay(firstDateStr, { month: 'short', day: 'numeric' }, locale)
  const lastFull = formatCalendarDay(lastDateStr, { month: 'short', day: 'numeric', year: 'numeric' }, locale)
  if (!first || !lastFull) return ''
  return `${first} – ${lastFull}`
}

/**
 * PROD-1330: the header of a day strip, from its first and last date-only day: "December 2026",
 * "November – December 2026" or "December 2026 – January 2027". Either day malformed -> ''.
 */
export const calendarStripLabel = (firstDateStr, lastDateStr, locale = 'en') => {
  const firstM = formatCalendarDay(firstDateStr, { month: 'long' }, locale)
  const lastM = formatCalendarDay(lastDateStr, { month: 'long' }, locale)
  const firstY = formatCalendarDay(firstDateStr, { year: 'numeric' }, locale)
  const lastY = formatCalendarDay(lastDateStr, { year: 'numeric' }, locale)
  if (!firstM || !lastM || !firstY || !lastY) return ''
  // PROD-1369: month + year come from Intl as one phrase (es 'diciembre de 2026'), never glued by hand.
  const MY = { month: 'long', year: 'numeric' }
  const firstMY = formatCalendarDay(firstDateStr, MY, locale)
  const lastMY = formatCalendarDay(lastDateStr, MY, locale)
  if (firstM === lastM && firstY === lastY) return lastMY
  if (firstY === lastY) return `${firstM} – ${lastMY}`
  return `${firstMY} – ${lastMY}`
}

/**
 * Format a date for display: "Jan 15, 2025"
 * @param {string|Date} dateString
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string}
 */
export const formatDate = (dateString, options = { month: 'short', day: 'numeric', year: 'numeric' }) => {
  if (!dateString) return ''
  const r = resolveDisplay(dateString)
  if (!r) return ''
  try {
    return r.date.toLocaleDateString(appLocale(), { ...options, timeZone: r.timeZone })
  } catch {
    return String(dateString)
  }
}

/**
 * Format a date with time: "Jan 15, 2025, 2:30 PM"
 * @param {string|Date} dateString
 * @returns {string}
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return ''
  const r = resolveDisplay(dateString)
  if (!r) return ''
  try {
    return r.date.toLocaleString(appLocale(), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: r.timeZone
    })
  } catch {
    return String(dateString)
  }
}

/**
 * Format date for appointment cards: { month: "MAR", day: 26, full: "Mar 26, 2025" }
 * @param {string} dateString
 * @returns {{ month: string, day: number, full: string }}
 */
export const formatAppointmentDate = (dateString) => {
  if (!dateString) return { month: '', day: '', full: 'Date TBD' }
  const r = resolveDisplay(dateString)
  if (!r) return { month: '', day: '', full: 'Date TBD' }
  const { date, timeZone } = r
  try {
    return {
      month: date.toLocaleDateString(appLocale(), { month: 'short', timeZone }).toUpperCase(),
      day: parseInt(date.toLocaleDateString(appLocale(), { day: 'numeric', timeZone })),
      full: date.toLocaleDateString(appLocale(), { month: 'short', day: 'numeric', year: 'numeric', timeZone })
    }
  } catch {
    return { month: '', day: '', full: 'Date TBD' }
  }
}

/**
 * Weekday + date for the appointment card info line (PROD-1136): "Mon, Oct 19" (en) / "lun, 19 oct" (es).
 * Same parse + same ORELLA_TZ + same appLocale() as formatAppointmentDate, so it can never
 * disagree with the dark date box (OCT 19) beside it.
 * @param {string|Date} dateString
 * @returns {string} '' when the date is missing or invalid
 */
export const formatAppointmentWeekdayDate = (dateString, timeZone = ORELLA_TZ) => {
  // PROD-1294: same shared rule as every sibling helper (see resolveDisplay).
  const r = resolveDisplay(dateString, timeZone)
  if (!r) return ''
  try {
    return r.date.toLocaleDateString(appLocale(), { weekday: 'short', month: 'short', day: 'numeric', timeZone: r.timeZone })
  } catch {
    return ''
  }
}

/**
 * Format a time string (HH:MM 24h) to 12h: "2:30 PM"
 * @param {string} timeString - Time in HH:MM format
 * @returns {string}
 */
export const formatAppointmentTime = (timeString) => {
  if (!timeString) return 'Time TBD'
  try {
    const raw = String(timeString).trim()
    // If already formatted like "8:00 AM", return as-is
    if (/^\d{1,2}:\d{2}\s*(AM|PM)$/i.test(raw)) return raw
    // Strip any trailing AM/PM before parsing (e.g. "08:00 AM" → "08:00")
    const cleaned = raw.replace(/\s*(AM|PM)\s*$/i, '').trim()
    const parts = cleaned.split(':')
    if (parts.length < 1) return 'Time TBD'
    const hour = parseInt(parts[0])
    if (isNaN(hour)) return 'Time TBD'
    const minutes = parts[1] || '00'
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  } catch {
    return 'Time TBD'
  }
}

/**
 * Format relative time: "2 hours ago", "Yesterday", "Mar 15"
 * @param {string|Date} dateString
 * @returns {string}
 */
export const formatTimeAgo = (dateString) => {
  if (!dateString) return ''
  const r = resolveDisplay(dateString)
  if (!r) return ''
  const date = r.date
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString(appLocale(), { month: 'short', day: 'numeric', timeZone: r.timeZone })
}

/**
 * Format date for medical documents: "01/15/2025" (MM/DD/YYYY)
 * @param {string|Date} dateString
 * @returns {string}
 */
// Deliberately NOT the app language: a fixed MM/DD/YYYY for medical documents (es-US would flip
// day and month). PROD-814 left it on 'en-US' on purpose; check-date-app-language.mjs pins that.
export const formatDateMedical = (dateString) => {
  if (!dateString) return ''
  const r = resolveDisplay(dateString)
  if (!r) return ''
  try {
    return r.date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', timeZone: r.timeZone })
  } catch {
    return String(dateString)
  }
}

/**
 * Format date for long display: "Monday, January 15"
 * @param {string|Date} dateString
 * @returns {string}
 */
export const formatDateLong = (dateString) => {
  if (!dateString) return ''
  const r = resolveDisplay(dateString)
  if (!r) return ''
  try {
    return r.date.toLocaleDateString(appLocale(), { weekday: 'long', month: 'long', day: 'numeric', timeZone: r.timeZone })
  } catch {
    return String(dateString)
  }
}

/**
 * Calculate age from date of birth (timezone-safe)
 * @param {string} dob
 * @returns {number|string}
 */
export const calculateAge = (dob) => {
  if (!dob) return 'Unknown'
  const birthDate = parseDate(dob)
  if (!birthDate) return 'Unknown'
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age >= 0 ? age : 'Unknown'
}
